import { BoxPlot } from "@visx/stats";
import { scaleLinear } from "@visx/scale";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type Props = {
  minimum: number;
  maximum: number;
  whiskerMin: number;
  firstQuartile: number;
  median: number;
  thirdQuartile: number;
  whiskerMax: number;
  mean: number | null;
  standardDeviation: number | null;
  validCount: number | null;
  copy: Record<string, string>;
};

function value(value: number) {
  const precision = Math.abs(value) > 0 && Math.abs(value) < 1 ? 4 : 2;
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: precision }).format(value);
}

export default function NumericBoxPlot(props: Props) {
  const { whiskerMin, firstQuartile, median, thirdQuartile, whiskerMax, mean, copy } = props;
  const padding = whiskerMin === whiskerMax ? Math.max(Math.abs(whiskerMin) * 0.05, 1) : 0;
  const scale = scaleLinear<number>({
    domain: [whiskerMin - padding, whiskerMax + padding],
    range: [10, 190],
    clamp: true,
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="mt-2 cursor-help rounded-md border border-border/70 bg-muted/15 px-1 py-0.5" tabIndex={0} aria-label={copy.box_plot}>
          <svg className="h-8 w-full overflow-visible" viewBox="0 0 200 32" preserveAspectRatio="none" aria-hidden="true">
            <BoxPlot
              horizontal
              top={6}
              boxWidth={20}
              min={whiskerMin}
              firstQuartile={firstQuartile}
              median={median}
              thirdQuartile={thirdQuartile}
              max={whiskerMax}
              valueScale={scale}
              fill="var(--primary)"
              fillOpacity={0.12}
              stroke="var(--primary)"
              strokeWidth={1.5}
              rx={3}
              medianProps={{ strokeWidth: 3 }}
            />
            {mean != null && <circle cx={scale(mean)} cy="16" r="3" fill="#f59e0b" stroke="var(--background)" strokeWidth="1.5" />}
          </svg>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6} className="w-52 px-3 py-2.5">
        <p className="mb-1.5 text-xs font-semibold">{copy.statistical_summary}</p>
        <dl className="space-y-0.5 text-[11px] tabular-nums">
          {props.validCount != null && <Stat label={copy.count} value={props.validCount.toLocaleString("pt-BR")} />}
          {mean != null && <Stat label={copy.mean} value={value(mean)} />}
          {props.standardDeviation != null && <Stat label={copy.standard_deviation} value={value(props.standardDeviation)} />}
          <Stat label={copy.minimum} value={value(props.minimum)} />
          <Stat label="25%" value={value(firstQuartile)} />
          <Stat label="50%" value={value(median)} />
          <Stat label="75%" value={value(thirdQuartile)} />
          <Stat label={copy.maximum} value={value(props.maximum)} />
        </dl>
      </TooltipContent>
    </Tooltip>
  );
}

function Stat({ label, value: displayValue }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{displayValue}</dd>
    </div>
  );
}
